import { afterNextRender, Component, computed, effect, ElementRef, inject, Injector, input, signal, viewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  DetailsMenuItem,
  HelpPanelComponent,
  isHelpShortcut,
  isSearchShortcut,
  ScreenService,
  SearchBoxComponent,
  User,
  UserNotificationsComponent,
  UserService,
  DropdownLoaderDirective,
} from 'shared';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { HELP_TOPICS, helpArticle, helpBasePath } from '@crowndesk-core/help/help-articles';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { CrowndeskMenuComponent } from '@crowndesk-core/crowndesk-menu/crowndesk-menu.component';
import { CustomerViewService } from '@crowndesk-core/customer-view/customer-view.service';
import { ImpersonationService } from '@crowndesk-core/login-as/impersonation.service';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { CapitalizePipe } from 'shared';
import { CROWNDESK_SEARCH_HIT_TYPES, searchTypesFor } from '@crowndesk-core/search-box/search-hit-types';
import { isSupplierEmployee } from '@crowndesk-core/access-rights/access-rights';
import { DialogService } from '@progress/kendo-angular-dialog';
import { AskQuestionDialogComponent } from '@crowndesk-core/ask-question/ask-question-dialog.component';

/** The languages of CrownDesk: the list in the top bar, and in the user settings for screens too narrow for it. */
export const CROWNDESK_LANGUAGES = [
  { value: 'nl', text: 'NL' },
  { value: 'fr', text: 'FR' },
];

@Component({
  selector: 'rc-top-bar',
  imports: [
    SearchBoxComponent,
    TranslateModule,
    UserNotificationsComponent,
    DropDownListComponent,
    CrowndeskMenuComponent,
    ButtonComponent,
    TooltipDirective,
    NgTemplateOutlet,
    CapitalizePipe,
    HelpPanelComponent,
    RouterLink,
    DropdownLoaderDirective,
  ],
  providers: [CapitalizePipe],
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.scss',
  host: {
    '(document:keydown)': 'onDocumentKeydown($event)',
  },
})
export class TopBarComponent {
  user = input.required<User>();
  userSettings = computed(() => this.user().settings);
  #translationService = inject(TranslateService);
  #screenService = inject(ScreenService);
  isMobile = computed(() => this.#screenService.isMobile());
  isTablet = computed(() => this.#screenService.isTablet());
  currentLanguage = computed(() => this.userSettings().language);
  menu = input<DetailsMenuItem[]>();
  #userService = inject(UserService);
  protected customerView = inject(CustomerViewService);
  #impersonation = inject(ImpersonationService);
  /** A Royal Crown employee is logged in as this client employee: the top bar says so, with a way to stop. */
  protected readonly isImpersonating = computed(() => !!this.#impersonation.clientEmployeeId());
  /** On mobile the search box only fits below the top bar; the search icon opens and closes it there. */
  isSearchOpen = signal(false);
  private readonly search = viewChild(SearchBoxComponent);
  protected readonly searchHitTypes = CROWNDESK_SEARCH_HIT_TYPES;
  protected readonly searchTypes = computed(() => searchTypesFor(this.user(), this.customerView.isActive()));
  /** The search finds client documents and pages, which supplier employees have no access to. */
  protected readonly hasSearch = computed(() => !isSupplierEmployee(this.user()));
  /** Questions go to a Royal Crown division on behalf of a client; the api only takes them from client employees. */
  protected readonly canAskQuestion = computed(() => !isSupplierEmployee(this.user()));
  #injector = inject(Injector);
  availableLanguages = CROWNDESK_LANGUAGES;
  constructor() {
    effect(() => {
      const lang = this.currentLanguage();
      if (lang) {
        this.#translationService.use(lang);
      }
    });
  }

  toggleSearch() {
    if (this.isSearchOpen()) {
      this.closeSearch();
      return;
    }
    this.isSearchOpen.set(true);
    afterNextRender(() => this.search()?.focus(), { injector: this.#injector });
  }

  closeSearch() {
    this.isSearchOpen.set(false);
  }

  /** The help opens at the right, with the manual of the current screen; it follows when the user navigates. */
  protected readonly isHelpOpen = signal(false);
  /** The manuals in the language the app shows (it follows a language switch at once). */
  readonly #shownLanguage = toSignal(this.#translationService.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.#translationService.currentLang,
  });
  protected readonly helpBasePath = computed(() => helpBasePath(this.#shownLanguage() ?? this.currentLanguage()));
  protected readonly helpTopics = HELP_TOPICS;
  #router = inject(Router);
  protected readonly helpArticle = toSignal(
    this.#router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => helpArticle(event.urlAfterRedirects)),
    ),
    { initialValue: helpArticle(this.#router.url) },
  );
  private readonly helpPanel = viewChild('helpPanel', { read: ElementRef });

  toggleHelp() {
    if (this.isHelpOpen()) {
      this.closeHelp();
    } else {
      this.openHelp();
    }
  }

  openHelp() {
    this.isHelpOpen.set(true);
    afterNextRender(() => this.helpPanel()?.nativeElement.focus(), { injector: this.#injector });
  }

  closeHelp() {
    this.isHelpOpen.set(false);
  }

  /**
   * Shift + F focuses the search (on mobile it opens it first), ? or F1 opens the help, except while typing, where they
   * are just a capital F or a question mark (F1 always opens the help).
   */
  onDocumentKeydown(event: KeyboardEvent) {
    if (isHelpShortcut(event)) {
      event.preventDefault();
      this.openHelp();
      return;
    }
    if (!isSearchShortcut(event) || !this.hasSearch()) return;
    event.preventDefault();
    if (this.isMobile()) {
      this.isSearchOpen.set(true);
    }
    afterNextRender(() => this.search()?.focus(), { injector: this.#injector });
  }

  #dialogService = inject(DialogService);
  #capitalizePipe = inject(CapitalizePipe);

  /** "Ask a question": a small form, mailed to the chosen Royal Crown division. */
  openAskQuestion() {
    this.#dialogService.open({
      content: AskQuestionDialogComponent,
      title: this.#capitalizePipe.transform(this.#translationService.instant('ASK_A_QUESTION')),
    });
  }

  /** Back to the selection of a client employee (the app loads the Royal Crown employee again). */
  stopImpersonation() {
    this.#impersonation.stop();
  }

  changeLanguage(language: string) {
    this.#translationService.use(language);
    const userSettings = this.userSettings();
    userSettings.language = language;
    this.#userService.updateUserMeSettings(this.user(), userSettings).subscribe();
  }
}
